import { useEffect, useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePaymentTransactions } from '@/hooks/usePaymentTransactions';
import { useUserProfiles } from '@/hooks/useUserProfiles';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Banknote, 
  CheckCircle2, 
  AlertCircle, 
  ExternalLink, 
  Loader2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  ArrowUpRight,
  ArrowDownLeft,
  FileText,
  HelpCircle,
  ShieldCheck,
  CreditCard,
  Mail,
  UserRoundCheck,
  Scale,
  RotateCcw,
  Clock,
  MessageSquareWarning
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import ConnectOnboarding from './ConnectOnboarding';
import { RefundRequestDialog } from '@/components/RefundRequestDialog';

const Payments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { transactions, disputes, refunds, loading: transactionsLoading, refetch, getFinancialSummary } = usePaymentTransactions();
  const { getUserProfile, profileCache } = useUserProfiles();

  // Stripe Connect State (moved from MyProfile)
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectStatus, setConnectStatus] = useState<{ 
    connected: boolean; 
    charges_enabled: boolean; 
    payouts_enabled: boolean; 
    details_submitted?: boolean; 
    account_id?: string 
  }>({
    connected: false,
    charges_enabled: false,
    payouts_enabled: false,
  });
  const [balance, setBalance] = useState<{ available: number; pending: number } | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [stripeError, setStripeError] = useState('');
  const [isMinorOnboardingOpen, setIsMinorOnboardingOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [refundRequests, setRefundRequests] = useState<any[]>([]);
  const [profileData, setProfileData] = useState<{ date_of_birth?: string | null }>({});
  
  // Pending earnings held on platform (for shell accounts awaiting onboarding)
  const [pendingPlatformEarnings, setPendingPlatformEarnings] = useState<number>(0);

  // Load user profiles for transactions
  useEffect(() => {
    const loadProfiles = async () => {
      const userIds = new Set<string>();
      transactions.forEach(t => {
        userIds.add(t.client_id);
        userIds.add(t.provider_id);
      });
      
      for (const userId of userIds) {
        if (!profileCache[userId]) {
          await getUserProfile(userId);
        }
      }
    };
    
    if (transactions.length > 0) {
      loadProfiles();
    }
  }, [transactions, getUserProfile, profileCache]);

  // Fetch refund requests
  useEffect(() => {
    const fetchRefundRequests = async () => {
      if (!user) return;
      try {
        const { data } = await supabase
          .from('refund_requests')
          .select('*')
          .order('created_at', { ascending: false });
        setRefundRequests(data || []);
      } catch (e) {
        console.error('Error fetching refund requests:', e);
      }
    };
    fetchRefundRequests();
  }, [user]);

  // Fetch profile data for DOB check
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      try {
        const { data } = await supabase
          .from('profiles')
          .select('date_of_birth, stripe_account_id, charges_enabled, payouts_enabled')
          .eq('id', user.id)
          .single();
        
        if (data) {
          setProfileData({ date_of_birth: data.date_of_birth });
          setConnectStatus({
            connected: !!data.stripe_account_id,
            charges_enabled: !!data.charges_enabled,
            payouts_enabled: !!data.payouts_enabled,
            details_submitted: data.charges_enabled || data.payouts_enabled,
            account_id: data.stripe_account_id || undefined,
          });
        }
      } catch (e) {
        console.error('Error fetching profile:', e);
      }
    };
    fetchProfile();
  }, [user]);

  // Fetch pending platform earnings (money held for shell accounts)
  useEffect(() => {
    const fetchPendingEarnings = async () => {
      if (!user) return;
      try {
        // Get completed transactions where this user is provider and transfer is pending
        const { data, error } = await supabase
          .from('payment_transactions')
          .select('provider_amount_cents')
          .eq('provider_id', user.id)
          .eq('status', 'completed')
          .in('transfer_status', ['pending', 'pending_onboarding']);
        
        if (error) {
          console.error('Error fetching pending earnings:', error);
          return;
        }
        
        const totalCents = (data || []).reduce((sum, t) => sum + (t.provider_amount_cents || 0), 0);
        setPendingPlatformEarnings(totalCents / 100);
      } catch (e) {
        console.error('Error fetching pending earnings:', e);
      }
    };
    fetchPendingEarnings();
  }, [user]);

  const calcAge = (isoDate?: string | null) => {
    if (!isoDate) return null;
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age;
  };

  const refreshConnectStatus = useCallback(async () => {
    if (!user) return;
    setStripeError('');
    try {
      const { data, error } = await supabase.functions.invoke('connect-account-status');
      if (error) throw error;
      
      if (data) {
        setConnectStatus({
          connected: !!data.connected,
          charges_enabled: !!data.charges_enabled,
          payouts_enabled: !!data.payouts_enabled,
          details_submitted: !!data.details_submitted,
          account_id: data.account_id,
        });
      }
    } catch (e: any) {
      console.error('[Payments] refreshConnectStatus error:', e);
      setStripeError('Unable to check payment status. Please try again.');
    }
  }, [user]);

  const fetchBalance = useCallback(async () => {
    if (!user) return;
    setBalanceLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-stripe-balance');
      if (error) throw error;
      
      if (data && data.connected) {
        // Ensure pending is never null - default to 0
        setBalance({
          available: (data.balance ?? 0) / 100,
          pending: (data.pending ?? 0) / 100
        });
        // Update connect status from the balance API response (source of truth)
        if (data.charges_enabled !== undefined || data.payouts_enabled !== undefined) {
          setConnectStatus(prev => ({
            ...prev,
            connected: true,
            charges_enabled: data.charges_enabled ?? prev.charges_enabled,
            payouts_enabled: data.payouts_enabled ?? prev.payouts_enabled,
          }));
        }
      } else {
        setBalance(null);
      }
    } catch (e: any) {
      console.error('[Payments] fetchBalance error:', e);
    } finally {
      setBalanceLoading(false);
    }
  }, [user]);

  const handleViewDashboard = useCallback(async () => {
    if (!user) return;
    setDashboardLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-stripe-dashboard-link');
      if (error) throw error;
      
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('No dashboard URL returned');
      }
    } catch (e: any) {
      console.error('[Payments] handleViewDashboard error:', e);
      toast.error('Unable to open Stripe dashboard. Please try again.');
    } finally {
      setDashboardLoading(false);
    }
  }, [user]);

  const handleSetupPayouts = async () => {
    if (!user || !profileData) return;

    const age = calcAge(profileData.date_of_birth);
    
    if (age === null) {
      toast.error('Please set your date of birth in your profile first');
      return;
    }

    if (age >= 18) {
      await startOnboarding();
    } else {
      setIsMinorOnboardingOpen(true);
    }
  };

  const startOnboarding = async () => {
    setConnectLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('connect-create-account');

      if (error) {
        toast.error('Failed to create onboarding link: ' + (error.message || 'Unknown error'));
        return;
      }

      if (data?.alreadyEnabled) {
        toast.success('Test account connected!');
        await refreshConnectStatus();
        return;
      }

      const url = data?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Failed to get onboarding link');
      }
    } catch (e: any) {
      toast.error('Unable to start payouts setup: ' + (e.message || 'Unknown error'));
    } finally {
      setConnectLoading(false);
    }
  };

  const resumeOnboarding = async () => {
    setConnectLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('connect-account-link');

      if (error) {
        toast.error('Failed to get resume link: ' + (error.message || 'Unknown error'));
        return;
      }

      const url = data?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Failed to get resume link');
      }
    } catch (e: any) {
      toast.error('Unable to resume payouts setup: ' + (e.message || 'Unknown error'));
    } finally {
      setConnectLoading(false);
    }
  };

  // Handle Stripe return from onboarding
  useEffect(() => {
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'return') {
      toast.info('Checking your Stripe account status...');
      refreshConnectStatus().then(() => {
        navigate('/transactions', { replace: true });
      });
    } else if (stripeParam === 'refresh') {
      toast.info('Your session expired. Please try again.');
      navigate('/transactions', { replace: true });
    }
  }, [searchParams, refreshConnectStatus, navigate]);

  // Fetch balance when connected. Stripe balances can change after new payments,
  // so we refresh on mount + poll while this page is open.
  useEffect(() => {
    if (!connectStatus.connected) return;

    fetchBalance();

    const interval = window.setInterval(() => {
      fetchBalance();
    }, 30000);

    return () => window.clearInterval(interval);
  }, [connectStatus.connected, fetchBalance]);


  // Handle minor onboarding message
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'stripe-onboarding-finished') {
        setIsMinorOnboardingOpen(false);
        refreshConnectStatus();
        toast.success('Stripe account connected!');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [refreshConnectStatus]);

  const summary = getFinancialSummary();

  const getStatusBadge = (status: string, type: 'transaction' | 'dispute' | 'refund' = 'transaction') => {
    const transactionVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      completed: 'default',
      pending: 'secondary',
      failed: 'destructive',
      refunded: 'outline',
    };
    
    const disputeVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      needs_response: 'destructive',
      under_review: 'secondary',
      won: 'default',
      lost: 'destructive',
      warning_needs_response: 'destructive',
      warning_under_review: 'secondary',
      warning_closed: 'outline',
      charge_refunded: 'outline',
    };
    
    const refundVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      succeeded: 'default',
      pending: 'secondary',
      failed: 'destructive',
      canceled: 'outline',
    };
    
    const variants = type === 'dispute' ? disputeVariants : type === 'refund' ? refundVariants : transactionVariants;
    return <Badge variant={variants[status] || 'secondary'}>{status.replace(/_/g, ' ')}</Badge>;
  };

  const getOtherPartyName = (transaction: any) => {
    if (!user) return 'Unknown';
    const otherPartyId = transaction.client_id === user.id ? transaction.provider_id : transaction.client_id;
    const profile = profileCache[otherPartyId];
    if (!profile) return 'Loading...';
    // Check if it's a fallback profile (deleted user)
    if (profile.name.startsWith('User (') && profile.name.endsWith('...)')) {
      return profile.name; // Show truncated ID for deleted users
    }
    return profile.name;
  };

  const isIncoming = (transaction: any) => {
    return user && transaction.provider_id === user.id;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <p className="text-muted-foreground">Please sign in to view your payments.</p>
          <Button onClick={() => navigate('/auth')} className="mt-4">Sign In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="mb-8">
          <h1 className="font-poppins font-bold text-4xl mb-4">Transactions</h1>
          <p className="font-inter text-xl text-muted-foreground">
            Manage your payouts, view transactions, and track earnings
          </p>
        </div>

        {/* Payout Setup Card - Show when there's money to withdraw OR pending platform earnings */}
        {(((balance?.available ?? 0) > 0 || pendingPlatformEarnings > 0) && !connectStatus.payouts_enabled) && (
          <Card className="mb-8 overflow-hidden border-2 border-green-500/50 bg-gradient-to-br from-green-500/10 via-emerald-500/5 to-teal-500/10 relative">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-green-400/20 via-transparent to-transparent pointer-events-none" />
            <CardContent className="pt-8 pb-8 relative">
              <div className="text-center space-y-6">
                {/* Celebration header */}
                <div className="space-y-2">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg shadow-green-500/30 mb-2 animate-pulse">
                    <DollarSign className="h-10 w-10 text-white" />
                  </div>
                  <h2 className="font-poppins font-bold text-3xl sm:text-4xl bg-gradient-to-r from-green-600 to-emerald-500 bg-clip-text text-transparent">
                    🎉 You've Got Money Waiting!
                  </h2>
                  <p className="text-muted-foreground text-lg max-w-md mx-auto">
                    Complete a quick 2-minute setup to claim your earnings
                  </p>
                </div>

                {/* Big money display */}
                <div className="py-6">
                  <p className="text-sm text-muted-foreground mb-2 uppercase tracking-wide font-medium">Ready to Claim</p>
                  <p className="text-6xl sm:text-7xl font-bold bg-gradient-to-r from-green-600 via-emerald-500 to-teal-500 bg-clip-text text-transparent">
                    ${((balance?.available ?? 0) + pendingPlatformEarnings).toFixed(2)}
                  </p>
                  {pendingPlatformEarnings > 0 && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Held securely until you complete setup
                    </p>
                  )}
                </div>

                {/* CTA Button */}
                <Button 
                  onClick={resumeOnboarding} 
                  disabled={connectLoading}
                  size="lg"
                  className="h-16 px-12 text-xl font-bold bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 hover:from-green-600 hover:via-emerald-600 hover:to-teal-600 shadow-xl shadow-green-500/30 hover:shadow-2xl hover:shadow-green-500/40 transition-all duration-300 hover:scale-105"
                >
                  {connectLoading ? (
                    <><Loader2 className="h-6 w-6 mr-3 animate-spin" /> Opening Stripe...</>
                  ) : (
                    <><Banknote className="h-6 w-6 mr-3" /> Claim Your ${((balance?.available ?? 0) + pendingPlatformEarnings).toFixed(2)} Now!</>
                  )}
                </Button>

                {/* Trust indicators */}
                <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground pt-4">
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck className="h-4 w-4 text-green-500" /> Secure setup
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-green-500" /> Takes 2 minutes
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CreditCard className="h-4 w-4 text-green-500" /> Powered by Stripe
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payouts enabled - show balance and dashboard link */}
        {connectStatus.connected && connectStatus.payouts_enabled && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-poppins text-2xl flex items-center gap-2">
                  <Banknote className="h-5 w-5" /> Payouts
                </CardTitle>
                <Badge variant="default">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Active
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Balance Display */}
              <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Available to Withdraw</p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {balanceLoading ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : balance ? (
                        `$${balance.available.toFixed(2)}`
                      ) : (
                        '$0.00'
                      )}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Funds are automatically transferred to your bank account on a rolling basis.
                </p>
              </div>

              <Button 
                onClick={handleViewDashboard} 
                className="w-full"
                disabled={dashboardLoading}
              >
                {dashboardLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Opening...</>
                ) : (
                  <><ExternalLink className="h-4 w-4 mr-2" /> Manage in Stripe Dashboard</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Analytics Summary */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                    <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Earned</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      ${summary.totalEarned.toFixed(2)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
                    <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Pending Earnings</p>
                    <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                      ${summary.pendingEarnings.toFixed(2)}
                    </p>
                    {summary.pendingTransactions > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {summary.pendingTransactions} transaction{summary.pendingTransactions > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
                    <TrendingDown className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Spent</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      ${summary.totalSpent.toFixed(2)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
          </div>
        )}

        {/* Transaction History */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-poppins text-2xl">Transaction History</CardTitle>
                <CardDescription>All payments you've sent and received</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {transactionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span>Loading transactions...</span>
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No transactions yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  When you send or receive payments, they'll appear here.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>With</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((transaction) => {
                      const incoming = isIncoming(transaction);
                      const refundRequest = refundRequests.find(r => r.transaction_id === transaction.id);
                      const hasRefundRequest = !!refundRequest;
                      const transactionDate = new Date(transaction.created_at);
                      const daysSincePurchase = Math.floor((Date.now() - transactionDate.getTime()) / (1000 * 60 * 60 * 24));
                      const canRequestRefund = !incoming && 
                        ['completed', 'pending'].includes(transaction.status) && 
                        !hasRefundRequest &&
                        daysSincePurchase <= 3;
                      
                      return (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {incoming ? (
                                <ArrowDownLeft className="h-4 w-4 text-green-600" />
                              ) : (
                                <ArrowUpRight className="h-4 w-4 text-blue-600" />
                              )}
                              <span className={incoming ? 'text-green-600' : 'text-blue-600'}>
                                {incoming ? 'Received' : 'Sent'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>{getOtherPartyName(transaction)}</TableCell>
                          <TableCell className="font-medium">
                            {incoming 
                              ? `+$${(transaction.provider_amount_cents / 100).toFixed(2)}`
                              : `-$${(transaction.amount_cents / 100).toFixed(2)}`
                            }
                          </TableCell>
                          <TableCell>{getStatusBadge(transaction.status, 'transaction')}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {format(new Date(transaction.created_at), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell>
                            {canRequestRefund && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedTransaction(transaction);
                                  setRefundDialogOpen(true);
                                }}
                              >
                                <MessageSquareWarning className="h-4 w-4 mr-1" />
                                Request Refund
                              </Button>
                            )}
                            {hasRefundRequest && refundRequest && (
                              <Badge
                                variant="outline"
                                className={
                                  ['refunded', 'partially_refunded'].includes(transaction.status)
                                    ? 'text-green-600 border-green-600'
                                    : refundRequest.status === 'denied'
                                      ? 'text-red-600 border-red-600'
                                      : 'text-amber-600 border-amber-600'
                                }
                              >
                                {['refunded', 'partially_refunded'].includes(transaction.status)
                                  ? 'Refunded'
                                  : refundRequest.status === 'denied'
                                    ? 'Refund Denied'
                                    : 'Refund Requested'}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Disputes & Refunds Section */}
        {(disputes.length > 0 || refunds.length > 0) && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="font-poppins text-2xl flex items-center gap-2">
                <Scale className="h-5 w-5" /> Disputes & Refunds
              </CardTitle>
              <CardDescription>Track status of any payment disputes or refunds</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Active Disputes */}
              {disputes.length > 0 && (
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    Disputes ({disputes.length})
                  </h3>
                  <div className="space-y-3">
                    {disputes.map((dispute) => {
                      const transaction = transactions.find(t => t.id === dispute.transaction_id);
                      const isUrgent = dispute.status === 'needs_response' || dispute.status === 'warning_needs_response';
                      const evidenceDue = dispute.evidence_due_by ? new Date(dispute.evidence_due_by) : null;
                      const daysUntilDue = evidenceDue ? Math.ceil((evidenceDue.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
                      
                      return (
                        <div 
                          key={dispute.id} 
                          className={`p-4 border rounded-lg ${isUrgent ? 'border-destructive bg-destructive/5' : 'border-border'}`}
                        >
                          <div className="flex items-start justify-between flex-wrap gap-2">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                {getStatusBadge(dispute.status, 'dispute')}
                                <span className="text-sm font-medium">
                                  ${(dispute.amount_cents / 100).toFixed(2)} {dispute.currency.toUpperCase()}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                Reason: {dispute.reason.replace(/_/g, ' ')}
                              </p>
                              {transaction && (
                                <p className="text-xs text-muted-foreground">
                                  Transaction from {format(new Date(transaction.created_at), 'MMM d, yyyy')}
                                </p>
                              )}
                            </div>
                            
                            {evidenceDue && isUrgent && (
                              <div className="text-right">
                                <div className="flex items-center gap-1 text-destructive text-sm">
                                  <Clock className="h-4 w-4" />
                                  <span className="font-medium">
                                    {daysUntilDue !== null && daysUntilDue > 0 
                                      ? `${daysUntilDue} days left` 
                                      : 'Due soon'}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  Evidence due {format(evidenceDue, 'MMM d, yyyy')}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {isUrgent && (
                            <div className="mt-3 p-2 bg-destructive/10 rounded text-sm text-destructive">
                              <strong>Action Required:</strong> This dispute requires a response. Contact support if you need assistance.
                            </div>
                          )}
                          
                          {dispute.status === 'won' && (
                            <div className="mt-3 p-2 bg-green-100 dark:bg-green-900/20 rounded text-sm text-green-700 dark:text-green-400">
                              ✓ Dispute resolved in your favor. Funds have been retained.
                            </div>
                          )}
                          
                          {dispute.status === 'lost' && (
                            <div className="mt-3 p-2 bg-red-100 dark:bg-red-900/20 rounded text-sm text-red-700 dark:text-red-400">
                              Dispute was decided against you. Funds were returned to the customer.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Refunds */}
              {refunds.length > 0 && (
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <RotateCcw className="h-4 w-4 text-blue-600" />
                    Refunds ({refunds.length})
                  </h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Amount</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {refunds.map((refund) => (
                          <TableRow key={refund.id}>
                            <TableCell className="font-medium">
                              ${(refund.amount_cents / 100).toFixed(2)} {refund.currency.toUpperCase()}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {refund.reason?.replace(/_/g, ' ') || 'N/A'}
                            </TableCell>
                            <TableCell>{getStatusBadge(refund.status, 'refund')}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {format(new Date(refund.created_at), 'MMM d, yyyy')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              
              {/* Help section */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-start gap-3">
                  <HelpCircle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Need help with a dispute?</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      If you have questions about a dispute or refund, please contact our support team. 
                      We can help you understand the process and provide guidance.
                    </p>
                    <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/feedback')}>
                      Contact Support
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tax Filing Guidance */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="font-poppins text-2xl flex items-center gap-2">
              <FileText className="h-5 w-5" /> Tax Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-start gap-3">
                <HelpCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">Tax Reporting Reminder</p>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    Income earned through this platform may be taxable. Keep records of all earnings for tax purposes.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-2">For US Residents</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Report income on Schedule C (Form 1040)</li>
                  <li>• Self-employment tax applies if earnings exceed $400</li>
                  <li>• You may receive a 1099-K from Stripe if applicable</li>
                  <li>• Keep records of all business expenses</li>
                </ul>
              </div>
              
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-2">For International Users</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Tax requirements vary by country</li>
                  <li>• Consult a local tax professional</li>
                  <li>• Keep records of all transactions</li>
                  <li>• Report foreign income as required</li>
                </ul>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate('/help/tax-faq')}>
                <HelpCircle className="h-4 w-4 mr-2" />
                View Tax FAQ
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Minor Onboarding Modal */}
        <Dialog open={isMinorOnboardingOpen} onOpenChange={setIsMinorOnboardingOpen}>
          <DialogContent className="max-w-2xl p-0">
            <DialogHeader className="sr-only">
              <DialogTitle>Payouts setup</DialogTitle>
            </DialogHeader>
            <ConnectOnboarding embed isPopup />
          </DialogContent>
        </Dialog>

        {/* Refund Request Dialog */}
        <RefundRequestDialog
          open={refundDialogOpen}
          onOpenChange={setRefundDialogOpen}
          transaction={selectedTransaction}
          onSuccess={() => {
            // Refresh refund requests
            supabase
              .from('refund_requests')
              .select('*')
              .order('created_at', { ascending: false })
              .then(({ data }) => setRefundRequests(data || []));
          }}
        />
      </div>
    </div>
  );
};

export default Payments;
